import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import type { DxReportPayload } from '../types';
import { DxReportPdfPane } from './DxReportPdfPane';
import { useDxReportPdf } from './useDxReportPdf';

interface DxReportPreviewProps {
    payload: DxReportPayload;
    isSubmitting: boolean;
    submitError: string | null;
    submitLabel: string;
    onBackToEdit: () => void;
    /** Caller takes the rendered Blob and dispatches it to the right endpoint. */
    onSubmit: (pdf: Blob) => Promise<void>;
    /** When true, force-disable the submit button regardless of PDF readiness. */
    submitDisabled?: boolean;
}

export function DxReportPreview({
    payload,
    isSubmitting,
    submitError,
    submitLabel,
    onBackToEdit,
    onSubmit,
    submitDisabled = false
}: DxReportPreviewProps) {
    const state = useDxReportPdf(payload);
    const canSubmit = state.kind === 'ready' && !isSubmitting && !submitDisabled;

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {/* Top bar */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                <button
                    onClick={onBackToEdit}
                    disabled={isSubmitting}
                    className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm disabled:opacity-40"
                >
                    <ArrowLeftIcon className="w-3.5 h-3.5" />
                    Back to editor
                </button>
                <div className="text-sm font-semibold text-gray-800">
                    Review &amp; submit Dx report
                </div>
                <div className="flex items-center gap-3">
                    {submitError && (
                        <span className="text-xs text-red-500 max-w-[280px] truncate">
                            {submitError}
                        </span>
                    )}
                    <button
                        onClick={() => state.kind === 'ready' && void onSubmit(state.blob)}
                        disabled={!canSubmit}
                        className="flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting && (
                            <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                        )}
                        <CheckCircleIcon className="w-4 h-4" />
                        {isSubmitting ? 'Submitting…' : submitLabel}
                    </button>
                </div>
            </div>

            <DxReportPdfPane state={state} onBackToEdit={onBackToEdit} />
        </div>
    );
}
